import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radio, Phone, Loader2, MapPin, User as UserIcon, Coffee, Flame,
  ClockAlert, Building2, Headphones, PhoneCall, MessageSquare, TrendingUp,
  Mic, Zap,
} from "lucide-react";

/**
 * Admin real-time console — shows every teammate currently on a call.
 * Uses the call-lock records (locked_by / locked_at / lock_expires_at) as
 * the source of truth. Polls every 3s so the manager sees who's live within
 * a heartbeat.
 */
const POLL_MS = 3000;

function fmtElapsed(lockedAt) {
  if (!lockedAt) return "00:00";
  const start = new Date(lockedAt).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const m = String(Math.floor(secs / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function fmtTimeLeft(expiresAt) {
  if (!expiresAt) return null;
  const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function LiveDialing() {
  const [feed, setFeed] = useState({ active: [], on_call: 0, idle: [], at: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);           // force 1s re-render for timers
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/live-dialing");
      setFeed(data);
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load live console";
      setError(msg);
      if (e?.response?.status === 403) toast.error("Admin/Manager access required");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  // 1-second re-render so the elapsed timer ticks live between polls
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="live-dialing-page">
      <header className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--app-text-muted)]">
            Live · updates every {POLL_MS / 1000}s
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Radio size={20} className="text-rose-500" />
            Live Dialing Console
          </h1>
          <p className="text-sm text-[var(--app-text-muted)] mt-1">
            Every rep with an active call-lock, in real time. Locks auto-expire after 3 minutes.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="card-flat px-4 py-2.5 text-center" data-testid="live-stat-on-call">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">On call</div>
            <div className="text-2xl font-bold text-rose-600">{feed.on_call}</div>
          </div>
          <div className="card-flat px-4 py-2.5 text-center" data-testid="live-stat-idle">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">Idle</div>
            <div className="text-2xl font-bold text-slate-500">{feed.idle?.length || 0}</div>
          </div>
        </div>
      </header>

      {loading && (
        <div className="card-flat p-10 text-center text-sm text-[var(--app-text-muted)]">
          <Loader2 className="animate-spin mx-auto mb-2" size={18} /> Loading live feed…
        </div>
      )}

      {!loading && error && (
        <div className="card-flat p-10 text-center text-sm text-rose-700" data-testid="live-error">
          {error}
        </div>
      )}

      {!loading && !error && feed.active.length === 0 && (
        <div className="card-flat p-14 text-center" data-testid="live-empty">
          <Coffee size={28} className="mx-auto text-[var(--app-text-muted)] mb-3" />
          <div className="font-display text-lg font-bold">Everyone&apos;s quiet right now</div>
          <p className="text-sm text-[var(--app-text-muted)] mt-1 max-w-md mx-auto">
            No teammates are on the phone. When someone starts a call this feed
            will light up within a few seconds.
          </p>
        </div>
      )}

      {!loading && !error && feed.active.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="live-active-grid">
          {feed.active.map((row) => {
            const elapsed = fmtElapsed(row.locked_at);
            const left = fmtTimeLeft(row.lock_expires_at);
            const isHot  = Number(row.motivation_score) >= 60;
            const isWarm = Number(row.motivation_score) >= 40 && !isHot;
            const stats = row.today_stats || { dials: 0, talks: 0, warms: 0, hots: 0 };

            const joinCall = async (mode) => {
              try {
                const { data } = await api.post(
                  `/admin/listen-in/${row.lead_id}`,
                  null,
                  { params: { mode } },
                );
                if (data?.can_barge) {
                  const label = mode === "whisper"
                    ? `Whispering to ${row.user_name} — only they can hear you`
                    : mode === "barge"
                    ? `Barging into ${row.user_name}'s call — everyone can hear you now`
                    : `Listening silently on ${row.user_name}'s call`;
                  toast.success(`${label}. ${data.message || ""}`);
                } else {
                  toast.info(data?.message ||
                    "Live coaching only works on multi-line power dials. Single-line direct calls can be reviewed via the call recording once the call ends.");
                }
              } catch (e) {
                toast.error(e?.response?.data?.detail || `Couldn't start ${mode}`);
              }
            };
            const onListenLive = () => joinCall("listen");
            const onWhisper    = () => joinCall("whisper");
            const onBarge      = () => joinCall("barge");

            return (
              <div key={row.lead_id}
                   className="card-flat p-4 md:p-5 border-l-4 border-rose-500 relative overflow-hidden"
                   data-testid={`live-row-${row.lead_id}`}>
                <div className="absolute top-3 right-3 flex items-center gap-1 text-[10px] uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse" />
                  <span className="text-rose-600 font-semibold">LIVE</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
                  <UserIcon size={11} />
                  <span data-testid="live-user-name">{row.user_name}</span>
                  <span className="text-[10px] opacity-60">· {row.user_role || "rep"}</span>
                </div>
                <h3 className="font-display text-lg font-bold mt-1 flex items-center gap-2 flex-wrap"
                    data-testid="live-lead-name">
                  {row.lead_name || row.phone || "Unknown lead"}
                  {isHot && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-widest flex items-center gap-1">
                      <Flame size={10} /> Hot {row.motivation_score}
                    </span>
                  )}
                  {isWarm && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-widest flex items-center gap-1">
                      Warm {row.motivation_score}
                    </span>
                  )}
                </h3>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[var(--app-text-muted)]">
                  {row.phone && (
                    <span className="inline-flex items-center gap-1 font-mono">
                      <Phone size={11} /> {row.phone}
                    </span>
                  )}
                  {row.property_address && (
                    <span className="inline-flex items-center gap-1 truncate max-w-[300px]">
                      <MapPin size={11} /> {row.property_address}
                    </span>
                  )}
                  {row.list_name && (
                    <span className="inline-flex items-center gap-1">
                      <Building2 size={11} /> {row.list_name}
                    </span>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-[auto,1fr] gap-4 items-stretch">
                  {/* Elapsed + TTL — left column */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]">
                        Elapsed
                      </div>
                      <div className="font-mono text-2xl font-bold text-rose-700 leading-none"
                           data-testid={`live-elapsed-${row.lead_id}`}>
                        {elapsed}
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="text-[9px] uppercase tracking-widest text-[var(--app-text-muted)]">
                        Lock TTL
                      </div>
                      <div className="text-[11px] font-medium text-[var(--app-text-muted)] inline-flex items-center gap-1">
                        <ClockAlert size={10} /> {left || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Rep's today stats — right column */}
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5"
                       data-testid={`live-rep-stats-${row.lead_id}`}>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1">
                      <TrendingUp size={10} />
                      {row.user_name?.split(" ")[0] || "Rep"} · today
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="text-center" title="Dials today">
                        <div className="text-[9px] uppercase text-slate-500 flex items-center justify-center gap-0.5">
                          <PhoneCall size={9} /> Dial
                        </div>
                        <div className="text-lg font-bold text-slate-800 leading-tight"
                             data-testid={`live-stat-dials-${row.lead_id}`}>
                          {stats.dials}
                        </div>
                      </div>
                      <div className="text-center" title="Live conversations today">
                        <div className="text-[9px] uppercase text-slate-500 flex items-center justify-center gap-0.5">
                          <MessageSquare size={9} /> Talk
                        </div>
                        <div className="text-lg font-bold text-emerald-700 leading-tight"
                             data-testid={`live-stat-talks-${row.lead_id}`}>
                          {stats.talks}
                        </div>
                      </div>
                      <div className="text-center" title="Warm leads today">
                        <div className="text-[9px] uppercase text-amber-600">Warm</div>
                        <div className="text-lg font-bold text-amber-700 leading-tight"
                             data-testid={`live-stat-warms-${row.lead_id}`}>
                          {stats.warms}
                        </div>
                      </div>
                      <div className="text-center" title="Hot leads today">
                        <div className="text-[9px] uppercase text-rose-600">Hot</div>
                        <div className="text-lg font-bold text-rose-700 leading-tight"
                             data-testid={`live-stat-hots-${row.lead_id}`}>
                          {stats.hots}
                        </div>
                      </div>
                    </div>

                    {/* Admin coaching bar — Listen / Whisper / Barge.
                        Always visible so admins can jump into any live call,
                        not just warm/hot ones. Each button rings the admin's
                        cell (from Settings → Profile → signer_phone) and
                        joins the SignalWire conference in the corresponding
                        LaML `<Conference>` mode:
                          • Listen  → muted="true" (silent monitor)
                          • Whisper → coach="<repCallSid>" (rep-only)
                          • Barge   → un-muted (full 2-way audio) */}
                    <div className="mt-2.5 grid grid-cols-3 gap-1.5" data-testid={`live-coach-bar-${row.lead_id}`}>
                      <button
                        type="button"
                        onClick={onListenLive}
                        className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300"
                        data-testid={`live-listen-${row.lead_id}`}
                        title="Silently listen — rep and seller hear nothing"
                      >
                        <Headphones size={11} />
                        Listen
                      </button>
                      <button
                        type="button"
                        onClick={onWhisper}
                        className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition bg-amber-500 text-white hover:bg-amber-600"
                        data-testid={`live-whisper-${row.lead_id}`}
                        title="Whisper to the rep — only they hear you, seller hears nothing"
                      >
                        <Mic size={11} />
                        Whisper
                      </button>
                      <button
                        type="button"
                        onClick={onBarge}
                        className="inline-flex items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition bg-rose-600 text-white hover:bg-rose-700"
                        data-testid={`live-barge-${row.lead_id}`}
                        title="Barge in — take over the call, everyone hears you"
                      >
                        <Zap size={11} />
                        Barge
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Idle reps */}
      {!loading && !error && (feed.idle?.length || 0) > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--app-text-muted)] mb-2">
            Idle reps
          </div>
          <div className="flex flex-wrap gap-2" data-testid="live-idle-list">
            {feed.idle.map((u) => {
              const s = u.today_stats || { dials: 0, talks: 0, warms: 0, hots: 0 };
              const hasActivity = s.dials > 0 || s.talks > 0;
              return (
                <div key={u.user_id}
                     className="rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 text-xs inline-flex items-center gap-1.5"
                     data-testid={`live-idle-${u.user_id}`}>
                  <Coffee size={11} />
                  {u.user_name || u.user_email}
                  <span className="text-[10px] opacity-70">· {u.user_role || "rep"}</span>
                  {hasActivity && (
                    <span className="text-[10px] font-mono ml-1 pl-2 border-l border-slate-300 text-slate-600"
                          title="Today · dials / talks / warm / hot">
                      {s.dials}d · {s.talks}t
                      {s.warms > 0 && <span className="text-amber-600"> · {s.warms}w</span>}
                      {s.hots > 0 && <span className="text-rose-600"> · {s.hots}🔥</span>}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
